import React, { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Close, Send } from '@mui/icons-material'
import moment from 'moment'
import API from '../../../services/API'
import useIsMobile from '../../../helpers/useIsMobile'

const getAuthorName = (message) =>
  [message?.author?.firstName, message?.author?.lastName]
    .filter(Boolean)
    .join(' ') ||
  message?.senderRole ||
  'PTS'

const PTSDutyChat = ({
  open,
  onClose,
  dutyID,
  title = 'PTS Chat',
  onMessageSent,
}) => {
  const isPhone = useIsMobile('sm')
  const [messages, setMessages] = useState([])
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [sendError, setSendError] = useState(false)
  const bottomRef = useRef(null)

  const loadMessages = async () => {
    if (!dutyID) return
    try {
      const res = await API.get(`/pts/duties/${dutyID}/chat`)
      if (res.status === 200) {
        setMessages(res.data?.messages || [])
        setLoadError(false)
      } else {
        setLoadError(true)
      }
    } catch (e) {
      setLoadError(true)
    }
  }

  useEffect(() => {
    if (!open || !dutyID) return undefined
    loadMessages()
    const interval = setInterval(loadMessages, 5000)
    return () => clearInterval(interval)
  }, [open, dutyID])

  useEffect(() => {
    if (!open) return
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [messages.length, open])

  const sendMessage = async () => {
    const message = draft.trim()
    if (!message || !dutyID) return
    setSending(true)
    setSendError(false)
    try {
      const res = await API.post(`/pts/duties/${dutyID}/chat`, { message })
      if (res.status === 200) {
        setMessages(res.data?.messages || [])
        setDraft('')
        onMessageSent?.(res.data?.messages || [])
      } else {
        setSendError(true)
      }
    } catch (e) {
      setSendError(true)
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isPhone}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography fontWeight={900}>{title}</Typography>
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          p: 1.5,
          bgcolor: '#F8FAFC',
          height: { xs: '60vh', sm: 420 },
        }}
      >
        <Stack spacing={1}>
          {loadError && (
            <Alert severity="warning">
              Unable to refresh messages — check your connection. Showing the
              last messages received.
            </Alert>
          )}
          {messages.length === 0 && (
            <Typography color="text.secondary" textAlign="center" mt={4}>
              No messages yet.
            </Typography>
          )}
          {messages.map((message) => {
            const fromControl = message.senderRole === 'Control'
            return (
              <Stack
                key={message._id || message.createdAt}
                direction="row"
                spacing={1}
                justifyContent={fromControl ? 'flex-start' : 'flex-end'}
                alignItems="flex-end"
              >
                {fromControl && (
                  <Avatar
                    src={message?.author?.photograph}
                    sx={{ width: 30, height: 30 }}
                  >
                    {getAuthorName(message)[0]}
                  </Avatar>
                )}
                <Box
                  sx={{
                    maxWidth: '78%',
                    p: 1.1,
                    borderRadius: 2,
                    bgcolor: fromControl ? '#fff' : '#0F766E',
                    color: fromControl ? '#0F172A' : '#fff',
                    border: fromControl ? '1px solid #E2E8F0' : 'none',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.88rem',
                      whiteSpace: 'pre-wrap',
                      color: fromControl ? '#0F172A' : '#fff',
                    }}
                  >
                    {message.message}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.4,
                      fontSize: '0.68rem',
                      color: fromControl
                        ? 'text.secondary'
                        : 'rgba(255,255,255,0.72)',
                    }}
                  >
                    {getAuthorName(message)} ·{' '}
                    {moment(message.createdAt).format('HH:mm')}
                  </Typography>
                </Box>
              </Stack>
            )
          })}
          <div ref={bottomRef} />
        </Stack>
      </DialogContent>
      {sendError && (
        <Alert severity="error" sx={{ mx: 1.25, mt: 1.25 }}>
          Message failed to send — check your connection and try again. Your
          draft has been kept.
        </Alert>
      )}
      <DialogActions sx={{ p: 1.25 }}>
        <TextField
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              sendMessage()
            }
          }}
          placeholder="Message control room"
          size="small"
          fullWidth
          multiline
          maxRows={3}
        />
        <Button
          variant="contained"
          onClick={sendMessage}
          disabled={sending || !draft.trim()}
          sx={{ minWidth: 44, height: 40 }}
        >
          <Send fontSize="small" />
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PTSDutyChat
